import { AddMemberRow } from '@/features/OrgMembers/components/AddMemberRow'
import {
  type EntryError,
  MAX_ROWS,
  type MemberRowPatch,
} from '@/features/OrgMembers/hooks/useMemberRows'
import type { MemberRow } from '@/features/OrgMembers/lib/memberRows'
import { Button, Field, HStack, Input, Stack } from '@chakra-ui/react'
import type { TFunction } from 'i18next'
import { type KeyboardEvent, useState } from 'react'
import { useTranslation } from 'react-i18next'

const KEY_PREFIX = 'orgDetails.members.addMembers'

type MembersEditorProps = Readonly<{
  rows: MemberRow[]
  entryError?: EntryError
  extraEntryErrorText?: string
  disabled: boolean
  onCommit: (text: string) => boolean
  onEntryChange?: (text: string) => void
  onChange: (key: string, patch: MemberRowPatch) => void
  onRemove: (key: string) => void
  onRetry: (key: string) => void
}>

export function entryErrorText(entryError: EntryError, t: TFunction): string {
  if (entryError.kind === 'invalid') {
    return String(
      t(`${KEY_PREFIX}.entry.errors.invalid`, { email: entryError.email }),
    )
  }
  return String(t(`${KEY_PREFIX}.entry.errors.${entryError.kind}`))
}

function shownErrorText(
  entryError: EntryError | undefined,
  extraEntryErrorText: string | undefined,
  t: TFunction,
): string | undefined {
  if (extraEntryErrorText !== undefined) {
    return extraEntryErrorText
  }
  if (entryError) {
    return entryErrorText(entryError, t)
  }
  return undefined
}

export function MembersEditor({
  rows,
  entryError,
  extraEntryErrorText,
  disabled,
  onCommit,
  onEntryChange,
  onChange,
  onRemove,
  onRetry,
}: MembersEditorProps) {
  const { t } = useTranslation('features', { useSuspense: false })
  const [entryText, setEntryText] = useState('')
  const errorText = shownErrorText(entryError, extraEntryErrorText, t)

  function commitEntry() {
    if (onCommit(entryText)) {
      setEntryText('')
    }
  }

  function handleEntryChange(text: string) {
    setEntryText(text)
    onEntryChange?.(text)
  }

  function handleEntryKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key !== 'Enter') {
      return
    }
    event.preventDefault()
    commitEntry()
  }

  return (
    <Stack gap={4}>
      <Field.Root invalid={errorText !== undefined} disabled={disabled}>
        <Field.Label>{t(`${KEY_PREFIX}.entry.label`)}</Field.Label>
        <HStack gap={2} width="100%">
          <Input
            type="email"
            value={entryText}
            disabled={disabled}
            placeholder={String(t(`${KEY_PREFIX}.entry.placeholder`))}
            onChange={(event) => handleEntryChange(event.target.value)}
            onKeyDown={handleEntryKeyDown}
          />
          <Button
            variant="outline"
            disabled={disabled || rows.length >= MAX_ROWS}
            onClick={commitEntry}
          >
            {t(`${KEY_PREFIX}.entry.add`)}
          </Button>
        </HStack>
        {errorText !== undefined && (
          <Field.ErrorText>{errorText}</Field.ErrorText>
        )}
      </Field.Root>
      {rows.map((row) => (
        <AddMemberRow
          key={row.key}
          row={row}
          disabled={disabled}
          onChange={onChange}
          onRemove={onRemove}
          onRetry={onRetry}
        />
      ))}
    </Stack>
  )
}
